import React, { FC, useState } from 'react';
import { pick } from 'lodash';
import { useTranslation } from 'react-i18next';
import { MutationFunction } from 'react-apollo';
import { Form, Formik, FormikProps } from 'formik';
import { Button, Divider, Grid, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';

import { S3ObjectInput, UpdateUserProfileComponent, UserProfileInput } from 'api';
import { Layouts, Loaders } from 'components';
import {
  withClientLocations,
  withClientDepartments,
  withSocialConnections,
} from 'providers/queries';
import { useCurrentUser, useNotify } from 'hooks';
import { NULL } from 'constants/index';

import { AccountSchema } from '../validations';
import { Access, Connections, Details, Notifications, ProfileImage } from '../components';

const styles = (theme: Theme) =>
  createStyles({
    '@global body': {
      backgroundColor: theme.palette.common.white,
    },
    root: {
      marginBottom: theme.spacing(4),
    },
    dividerFirst: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
    divider: {
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(4),
    },
  });

const enhancers = compose<WithStyles<typeof styles>, {}>(
  withClientLocations,
  withClientDepartments,
  withSocialConnections(),
  withStyles(styles),
);

const EditAccount: FC<WithStyles<typeof styles>> = ({ classes }) => {
  const [working, setWorking] = useState(false);
  const { t } = useTranslation();

  const notify = useNotify();
  const currentUser = useCurrentUser();

  const initialValues = pick(currentUser, [
    'id',
    'full_name',
    'job_title',
    'email_address',
    'location_id',
    'department_id',
    'image',
    'notification_delivery_types',
  ]);

  function handleError() {
    setWorking(false);
    notify.mutationError();
  }

  function handleCompleted() {
    setWorking(false);
    return NULL;
  }

  function handleSubmit(updateUserProfile: MutationFunction, values: UserProfileInput) {
    const { department_id, location_id, image, ...rest } = values;
    const data: UserProfileInput = {
      image,
      department_id: department_id ? department_id : null,
      location_id: location_id ? location_id : null,
      ...rest,
    };

    if (image && !image.localUri) {
      delete data.image;
    }

    setWorking(true);

    return updateUserProfile({ variables: { data } });
  }

  function handleSave(image: S3ObjectInput, formikProps: FormikProps<UserProfileInput>) {
    const { setFieldValue } = formikProps;
    setFieldValue('image', image);
  }

  function renderSubmit() {
    if (working) {
      return <Loaders.Standard height={38} />;
    }

    return (
      <Button color="secondary" size="large" type="submit" variant="contained">
        {t('components:saveChanges')}
      </Button>
    );
  }

  function renderMain(formik: FormikProps<UserProfileInput>) {
    return (
      <div className={classes.root}>
        <Typography color="inherit" variant="h4">
          {t('user:accountSettings')}
        </Typography>
        <Divider className={classes.dividerFirst} />
        <Details />
        <Divider className={classes.divider} />
        <Access />
        <Divider className={classes.divider} />
        <ProfileImage onSave={image => handleSave(image, formik)} />
        <Divider className={classes.divider} />
        <Connections />
        <Divider className={classes.divider} />
        <Notifications />
        <Divider className={classes.divider} />
        <Grid container justify="flex-end">
          <Grid item>{renderSubmit()}</Grid>
        </Grid>
      </div>
    );
  }

  return (
    <UpdateUserProfileComponent onError={handleError} onCompleted={handleCompleted}>
      {updateUserProfile => (
        <Formik
          onSubmit={values => handleSubmit(updateUserProfile, values)}
          initialValues={initialValues}
          validationSchema={AccountSchema}>
          {(formikProps: FormikProps<UserProfileInput>) => (
            <Form>
              <Layouts.App1Column
                desktopSize={11}
                main={<Layouts.AppMain1Column main={renderMain(formikProps)} />}
              />
            </Form>
          )}
        </Formik>
      )}
    </UpdateUserProfileComponent>
  );
};

export default enhancers(EditAccount);
